import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { useQueueDispatch } from '../contexts/QueueDispatchContext';
import {
  agentLanguageCode,
  callEntry,
  formatEta,
  fullName,
  partyMarker,
  sameLanguage,
  setDeparturePlayLanguage,
  uncallEntry,
} from '../services/queue';
import { byPcName, networkLoad, pickNextPc } from '../services/dispatchPick';
import LanguageFlag from './LanguageFlag';
import QueuePickerModal from './QueuePickerModal';
import type { ConfigurationRelease, QueueEntry, StartedEntry } from '../types/database';
import type { ToastData } from './Toast';

// "Volgende bezoeker" — the queue-first dispatch pop-up. Opens automatically
// when a PC becomes free while visitors wait (QueueDispatchContext trigger),
// or manually from the NextUpWidget. Flow: visitor (queue selection: override
// else head; or a PINNED started visitor being moved from another PC) →
// experience (booked picks first, the room's other releases after a divider)
// → language (preselected from the departure's play language, persisted when
// changed/unconfirmed) → free PC (auto-pick, manual override) → one Start
// button = launch through the tile + dequeue (with the PC id).

interface Props {
  /** Dashboard's per-PC installed-release map (room config ∩ pc_sync_status). */
  pcExperiencesMap: Map<string, ConfigurationRelease[]>;
  onToast: (t: ToastData) => void;
  /** Multiplayer / voice-chat releases go through the MultiplayerSessionModal;
   *  the queue entry ids are dequeued once that session is active. */
  onStartMultiplayer?: (
    release: ConfigurationRelease,
    pcId: string,
    dequeueEntryIds: string[],
  ) => void;
}

type DispatchEntry = QueueEntry | StartedEntry;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function operatorIdFromSession(): string | null {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : null;
  } catch {
    return null;
  }
}

/** Just the player index out of a label: an operator sees "6", an admin sees
 *  "6: ANDROMEDA-01" and the big number should still be a number. */
function shortPcLabel(label: string): string {
  const head = label.split(':')[0]?.trim();
  return head && head.length <= 3 ? head : label;
}

// byPcName / networkLoad / pickNextPc live in services/dispatchPick so the bar
// and the wachtrij answer "which PC is next" with the SAME rule this pop-up
// acts on -- see the header there.

export default function DispatchModal({ pcExperiencesMap, onToast, onStartMultiplayer }: Props) {
  const queue = useQueueDequeue();
  const dispatch = useQueueDispatch();

  const pinned = dispatch.pinnedVisitor;
  // Pinned started visitor (move) else the queue selection (override else head).
  const entry: DispatchEntry | null = pinned ? pinned.entry : queue.selectedEntry;
  const open = dispatch.dispatchOpen;

  // --- Experience resolution -------------------------------------------------
  // Distinct experience ids on the booking (the per-person model doesn't map a
  // person to one pick, so ambiguity = the operator decides).
  const bookedPicks = useMemo(() => {
    const picks = entry?.experiences ?? [];
    const seen = new Set<string>();
    return picks.filter((p) => {
      if (seen.has(p.experience_id)) return false;
      seen.add(p.experience_id);
      return true;
    });
  }, [entry]);

  // All launchable releases anywhere in the room, keyed by experience_id — the
  // pool the booked experience is matched against (room config pins versions).
  const roomReleases = useMemo(() => {
    const byExp = new Map<string, ConfigurationRelease>();
    for (const releases of pcExperiencesMap.values()) {
      for (const r of releases) {
        if (!byExp.has(r.experience_id)) byExp.set(r.experience_id, r);
      }
    }
    return byExp;
  }, [pcExperiencesMap]);

  // The room's releases that are NOT on the booking, after the divider.
  const otherReleases = useMemo(() => {
    const booked = new Set(bookedPicks.map((p) => p.experience_id));
    return [...roomReleases.values()]
      .filter((r) => !booked.has(r.experience_id))
      .sort((a, b) => (a.experiences?.name ?? '').localeCompare(b.experiences?.name ?? ''));
  }, [roomReleases, bookedPicks]);

  const [pickedExperienceId, setPickedExperienceId] = useState<string | null>(null);
  const [pickedLanguage, setPickedLanguage] = useState<string | null>(null);
  const [pickedPcId, setPickedPcId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Kid: the room rule is 62 mm IPD for everyone (the agent re-sends it to the
  // headset every 30 s), but the software setter stops at 62 while a child
  // needs the buttons on the headset (down to 58). Ticking this suspends the
  // lock on the chosen PC for this run; the agent puts it back when the run ends.
  const [kid, setKid] = useState(false);

  // Auto-select the experience: a pinned move keeps what was running; an
  // unambiguous booking → its experience; else wait for the operator.
  const autoExperienceId =
    pinned?.experienceId && roomReleases.has(pinned.experienceId)
      ? pinned.experienceId
      : bookedPicks.length === 1 && roomReleases.has(bookedPicks[0].experience_id)
        ? bookedPicks[0].experience_id
        : null;
  const effectiveExperienceId = pickedExperienceId ?? autoExperienceId;
  const release = effectiveExperienceId ? roomReleases.get(effectiveExperienceId) ?? null : null;
  const deviatesFromBooking =
    !!effectiveExperienceId &&
    bookedPicks.length > 0 &&
    !bookedPicks.some((p) => p.experience_id === effectiveExperienceId);

  const isMultiplayer = !!(release?.experiences?.is_multiplayer || release?.experiences?.use_vc);
  const supportedLanguages = release?.experiences?.supported_languages ?? [];
  const languageCodes = supportedLanguages
    .map((sl) => sl.language?.short_name ?? null)
    .filter((c): c is string => !!c);

  // Preselect: the language that was running (move) else the departure's play
  // language, when the release supports it. Codes compare case-insensitively
  // (DB lowercase, public.languages UPPERCASE).
  const preferredCode = pinned?.language ?? agentLanguageCode(entry?.play_language);
  const prefillLanguage =
    languageCodes.find((c) => sameLanguage(c, preferredCode)) ?? null;
  const autoLanguage = languageCodes.length === 1 ? languageCodes[0] : prefillLanguage;
  const effectiveLanguage = pickedLanguage ?? autoLanguage;
  const needsLanguage = languageCodes.length > 1 && !effectiveLanguage;

  // --- Free-PC table ----------------------------------------------------------
  const freePcs = useMemo(() => {
    const rows: {
      pcId: string;
      /** Machine name. Sorting and the undo record use this. */
      pcName: string;
      /** What the operator SEES on that PC's bar: the bare player index, or
       *  "2: MONA-02" for an admin. Everything the operator reads uses this,
       *  or they cannot match the pop-up to the dashboard in front of them. */
      pcLabel: string;
      canLaunch: boolean;
      hasExperience: boolean;
    }[] = [];
    for (const [pcId, r] of dispatch.pcReports) {
      // `recovering`: the experience died but the previous visitor is still on
      // this PC (relaunch in flight, or the agent gave up and nobody has
      // decided what happens to them yet). It reads as free, but the next
      // visitor must not be sent there.
      if (!r.online || r.running || r.recovering) continue;
      rows.push({
        pcId,
        pcName: r.pcName,
        pcLabel: r.pcLabel ?? r.pcName,
        canLaunch: r.canLaunch,
        hasExperience: effectiveExperienceId
          ? (pcExperiencesMap.get(pcId) ?? []).some(
              (rel) => rel.experience_id === effectiveExperienceId,
            )
          : true,
      });
    }
    return rows.sort(byPcName);
  }, [dispatch.pcReports, pcExperiencesMap, effectiveExperienceId]);

  // How many PCs each network is already carrying. Headsets stream over WiFi,
  // so five visitors on one router and none on the other costs picture quality
  // even though both rooms have free PCs (Dirk 2026-09-10). A PC counts as
  // load while it is running OR recovering.
  const loadPerNetwork = useMemo(
    () => networkLoad(dispatch.pcReports, dispatch.networkOf),
    [dispatch.pcReports, dispatch.networkOf],
  );

  const networkLoadOf = useCallback(
    (pcId: string) => loadPerNetwork.get(dispatch.networkOf(pcId) ?? 'unknown') ?? 0,
    [loadPerNetwork, dispatch.networkOf],
  );

  // When this PC was last used, so the pick can favour the one that has been
  // resting longest. A PC with no run today has never been used and sorts
  // oldest, which is right: its headset has had all day to be cleaned.
  const lastUsedAt = useCallback(
    (pcId: string): number => {
      const started = queue.startedByPc.get(pcId);
      if (!started) return 0;
      const t = Date.parse(started.last_event_at ?? started.started_at);
      return Number.isFinite(t) ? t : 0;
    },
    [queue.startedByPc],
  );

  // Auto-pick, in order: the least loaded router, then the PC that has been
  // idle longest, then name order so the choice is stable across refetches.
  //
  // Spreading stays first because headsets stream over WiFi and five visitors
  // on one router costs picture quality. Longest-idle replaced "prefer the PC
  // that just came free" (Dirk 2026-09-11): the just-freed PC is the one whose
  // headset is dirtiest and least charged, so preferring it meant staff were
  // always cleaning under time pressure while rested stations sat unused.
  const autoPcId = useMemo(
    () =>
      pickNextPc({
        candidates: freePcs,
        networkLoadOf,
        lastUsedAt,
        excludePcId: pinned?.fromPcId ?? null,
      }),
    [freePcs, pinned, networkLoadOf, lastUsedAt],
  );
  const effectivePcId = pickedPcId ?? autoPcId;
  const effectivePc = freePcs.find((p) => p.pcId === effectivePcId) ?? null;

  // Reset local picks whenever the pop-up (re)opens or the visitor changes.
  const entryId = entry?.queue_entry_id ?? null;

  // Calling the visitor forward happens HERE, not at launch: they have to walk
  // up before the operator can check their experience and language with them.
  // The cleanup takes the call back when the pop-up closes or moves to someone
  // else; a launched row leaves the queue anyway and uncall tolerates that.
  // A pinned move is a visitor who is already playing, so it never calls.
  useEffect(() => {
    if (!open || pinned || !entryId) return;
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    void callEntry(entryId, operatorId).catch((err) => {
      console.error('call forward failed', err);
    });
    return () => {
      void uncallEntry(entryId, operatorId).catch(() => {
        /* best effort: the call expires on its own */
      });
    };
  }, [open, pinned, entryId]);
  useEffect(() => {
    setPickedExperienceId(null);
    setPickedLanguage(null);
    setPickedPcId(null);
    setStarting(false);
    setPickerOpen(false);
    setKid(false);
  }, [open, entryId]);

  if (!open) return null;

  const canStart =
    !!entry && !!release && !!effectivePc && effectivePc.canLaunch &&
    !isMultiplayer && !needsLanguage && !starting;
  const canStartMultiplayer =
    !!entry && !!release && isMultiplayer && !!effectivePc && effectivePc.canLaunch &&
    !!onStartMultiplayer && !starting;

  /** Records the language the visitor actually gets on the departure when it
   *  differs from the prefill or was never confirmed. Best effort: a failure
   *  must not block the launch. */
  async function persistLanguage(e: DispatchEntry, language: string | null) {
    if (!language) return;
    if (e.play_language_confirmed && sameLanguage(language, e.play_language)) return;
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    try {
      await setDeparturePlayLanguage(e.departure_id, language, operatorId);
    } catch (err) {
      console.error('setDeparturePlayLanguage failed', err);
      onToast({ kind: 'error', message: 'Taal niet opgeslagen op de boeking (start gaat door).' });
    }
  }

  /** Tells the chosen PC's agent whether this run is a kid (IPD lock off) or
   *  not (62 mm, sent right now so the headset is ready when it goes on).
   *  Never blocks the launch; a kid flag that did not land is worth a toast
   *  because the lock would then undo every button press. */
  async function applyIpdMode(pc: { pcId: string; pcLabel: string }) {
    const ok = await dispatch.setHmdIpdOn(pc.pcId, kid);
    if (!ok && kid) {
      onToast({
        kind: 'error',
        message: `Kind-stand niet gezet op ${pc.pcLabel}: de bril blijft op 62 mm. Zet hem alsnog via het headset-venster.`,
      });
    }
  }

  async function start() {
    if (!entry || !release || !effectivePc) return;
    setStarting(true);
    await persistLanguage(entry, effectiveLanguage);
    await applyIpdMode(effectivePc);
    const ok = await dispatch.launchOn(
      { pcId: effectivePc.pcId, pcName: effectivePc.pcName },
      release,
      effectiveLanguage,
      {
        queueEntryId: entry.queue_entry_id,
        departureId: entry.departure_id,
        customerName: fullName(entry),
      },
      // A pinned dispatch IS the "Verplaats naar andere PC" flow: this visitor
      // is already started elsewhere, and re-pointing them is the intent.
      { allowMove: !!pinned },
    );
    setStarting(false);
    if (ok) {
      if (pinned) dispatch.setPendingMove(null);
      queue.refresh();
      onToast({
        kind: 'success',
        message: pinned
          ? `${fullName(entry)} verplaatst naar ${effectivePc.pcLabel}`
          : `${fullName(entry)} gestart op ${effectivePc.pcLabel}`,
      });
      dispatch.closeDispatch();
    } else {
      onToast({
        kind: 'error',
        message: `Starten op ${effectivePc.pcLabel} mislukt. Kies eventueel een andere PC.`,
      });
    }
  }

  async function startMultiplayer() {
    if (!entry || !release || !effectivePc || !onStartMultiplayer) return;
    setStarting(true);
    await persistLanguage(entry, effectiveLanguage);
    await applyIpdMode(effectivePc);
    setStarting(false);
    if (pinned) dispatch.setPendingMove(null);
    onStartMultiplayer(release, effectivePc.pcId, [entry.queue_entry_id]);
    dispatch.closeDispatch();
  }

  const eta = entry && 'eta_minutes' in entry ? entry.eta_minutes : null;
  const marker = entry ? partyMarker(entry) : '';

  return (
    <div className="queue-picker-overlay" onClick={dispatch.closeDispatch}>
      <div className="queue-picker-modal dispatch-modal" onClick={(e) => e.stopPropagation()}>
        <header className="queue-picker-header">
          <h2>{pinned ? 'Verplaats bezoeker' : 'Volgende bezoeker'}</h2>
          <button className="queue-picker-close" onClick={dispatch.closeDispatch} aria-label="Sluiten">
            ✕
          </button>
        </header>

        {!entry ? (
          <div className="queue-picker-empty">De wachtrij is leeg.</div>
        ) : (
          <div className="dispatch-body">
            <div className="dispatch-visitor">
              <div className="dispatch-visitor-text">
              <span className="dispatch-visitor-name">
                <LanguageFlag code={entry.play_language} className="dispatch-visitor-flag" />
                {fullName(entry)}
                {marker ? <span className="dispatch-visitor-party">{marker}</span> : null}
                {'parked' in entry && entry.parked ? (
                  <span className="queue-parked-pill">even weg</span>
                ) : null}
              </span>
              <span className="dispatch-visitor-meta">
                {pinned
                  ? `gestopt op ${pinned.fromPcLabel}`
                  : `geboekt ${formatTime(entry.departure_start)}`}
                {!pinned && eta !== null && eta > 0 ? ` · nog ${formatEta(eta)}` : ''}
                {entry.experience_names ? ` · ${entry.experience_names}` : ''}
              </span>
              {!pinned ? (
                <button
                  type="button"
                  className="next-up-edit-link dispatch-other-link"
                  onClick={() => setPickerOpen(true)}
                >
                  Andere bezoeker…
                </button>
              ) : null}
              </div>
              {/* The single most useful thing in this pop-up: WHICH headset to
                  pick up. It was only legible as a highlighted chip halfway
                  down the list, while the top-right of this box sat empty
                  (Dirk 2026-09-16). */}
              {effectivePc ? (
                <div className="dispatch-visitor-pc" title={effectivePc.pcName}>
                  <span className="dispatch-visitor-pc-num">{shortPcLabel(effectivePc.pcLabel)}</span>
                  <span className="dispatch-visitor-pc-cap">PC</span>
                </div>
              ) : null}
            </div>

            {/* Experience: booked picks first, the room's other releases after a divider */}
            <div className="dispatch-section">
              <span className="dispatch-section-label">
                {bookedPicks.length > 1 ? 'Welke ervaring?' : 'Ervaring'}
              </span>
              <div className="dispatch-chips">
                {bookedPicks.map((p) => (
                  <button
                    key={p.experience_id}
                    className={`dispatch-chip dispatch-chip--booked ${effectiveExperienceId === p.experience_id ? 'selected' : ''}`}
                    onClick={() => { setPickedExperienceId(p.experience_id); setPickedLanguage(null); setPickedPcId(null); }}
                    disabled={!roomReleases.has(p.experience_id)}
                    title={roomReleases.has(p.experience_id) ? 'Geboekte ervaring' : 'Niet beschikbaar in deze ruimte'}
                  >
                    {p.name}
                    <span className="dispatch-chip-tag">geboekt</span>
                  </button>
                ))}
                {bookedPicks.length > 0 && otherReleases.length > 0 ? (
                  <span className="dispatch-chips-divider" aria-hidden="true" />
                ) : null}
                {otherReleases.map((r) => (
                  <button
                    key={r.experience_id}
                    className={`dispatch-chip ${effectiveExperienceId === r.experience_id ? 'selected' : ''}`}
                    onClick={() => { setPickedExperienceId(r.experience_id); setPickedLanguage(null); setPickedPcId(null); }}
                  >
                    {r.experiences?.name ?? 'Onbekend'}
                  </button>
                ))}
              </div>
              {bookedPicks.length === 0 && !pinned ? (
                <span className="dispatch-warning">
                  Geen geboekte ervaring gevonden. Kies handmatig.
                </span>
              ) : null}
              {deviatesFromBooking ? (
                <span className="dispatch-hint">
                  Wijkt af van boeking ({bookedPicks.map((p) => p.name).join(' + ')})
                </span>
              ) : null}
            </div>

            {/* Language chips whenever the release supports 2+ languages */}
            {languageCodes.length > 1 && (
              <div className="dispatch-section">
                <span className="dispatch-section-label">Taal</span>
                <div className="dispatch-chips">
                  {languageCodes.map((code) => (
                    <button
                      key={code}
                      className={`dispatch-chip dispatch-chip--lang ${sameLanguage(effectiveLanguage, code) ? 'selected' : ''}`}
                      onClick={() => setPickedLanguage(code)}
                    >
                      <LanguageFlag code={code} className="dispatch-chip-flag" />
                      {code.toUpperCase()}
                    </button>
                  ))}
                </div>
                {!entry.play_language_confirmed ? (
                  <span className="dispatch-hint">Taal nog niet bevestigd bij check-in</span>
                ) : null}
              </div>
            )}

            {/* Headset IPD: 62 mm for everyone unless this is a kid */}
            <div className="dispatch-section">
              <span className="dispatch-section-label">Bril (IPD)</span>
              <div className="dispatch-chips">
                <button
                  type="button"
                  className={`dispatch-chip ${!kid ? 'selected' : ''}`}
                  onClick={() => setKid(false)}
                  title="De bril wordt automatisch op 62 mm gehouden"
                >
                  Volwassene · 62 mm
                </button>
                <button
                  type="button"
                  className={`dispatch-chip dispatch-chip--kid ${kid ? 'selected' : ''}`}
                  onClick={() => setKid(true)}
                  title="Zet de automatische 62 mm uit voor deze beurt; de IPD wordt met de knoppen op de bril ingesteld (tot 58 mm)"
                >
                  Kind · IPD handmatig
                </button>
              </div>
              {kid ? (
                <span className="dispatch-hint">
                  De bril houdt de IPD deze beurt niet vast: stel hem in met de knoppen op de bril (tot 58 mm).
                  {' '}Na de ervaring gaat hij vanzelf terug naar 62 mm.
                </span>
              ) : null}
            </div>

            {/* Free PCs */}
            <div className="dispatch-section">
              <span className="dispatch-section-label">Vrije PC</span>
              {freePcs.length === 0 ? (
                <span className="dispatch-warning">Geen vrije PC's op dit moment.</span>
              ) : (
                <div className="dispatch-pc-grid">
                  {freePcs.map((p) => (
                    <button
                      key={p.pcId}
                      className={`dispatch-pc ${effectivePcId === p.pcId ? 'selected' : ''} ${!p.canLaunch ? 'blocked' : ''}`}
                      onClick={() => setPickedPcId(p.pcId)}
                      disabled={!p.canLaunch}
                      title={
                        !p.canLaunch
                          ? 'PC-checks niet in orde (headset/diagnose/sync)'
                          : !p.hasExperience
                            ? 'Ervaring niet geïnstalleerd op deze PC'
                            : pinned && p.pcId === pinned.fromPcId
                              ? 'Dezelfde PC als waar de bezoeker gestopt is'
                              : undefined
                      }
                    >
                      {p.pcLabel}
                      {!p.hasExperience && p.canLaunch && <span className="dispatch-pc-flag">!</span>}
                    </button>
                  ))}
                </div>
              )}
              {effectivePc && !effectivePc.hasExperience && (
                <span className="dispatch-warning">
                  Ervaring niet geïnstalleerd op {effectivePc.pcLabel}. Starten kan mislukken.
                </span>
              )}
            </div>

            {isMultiplayer && (
              <div className="dispatch-section">
                <span className="dispatch-hint">
                  Multiplayer-ervaring: de sessie wordt via het multiplayer-venster gestart.
                  {' '}De bezoeker verlaat de wachtrij zodra de sessie loopt.
                </span>
                <button
                  className="queue-picker-primary dispatch-mp-btn"
                  onClick={() => void startMultiplayer()}
                  disabled={!canStartMultiplayer}
                  title={!onStartMultiplayer ? 'Niet beschikbaar op dit scherm' : undefined}
                >
                  Start multiplayer{effectivePc ? ` (${effectivePc.pcLabel})` : ''}
                </button>
              </div>
            )}
          </div>
        )}

        <footer className="queue-picker-footer">
          <button className="queue-picker-secondary" onClick={dispatch.closeDispatch}>
            Later
          </button>
          <button className="queue-picker-primary" onClick={() => void start()} disabled={!canStart}>
            {starting
              ? 'Starten…'
              : effectivePc
                ? pinned
                  ? `Verplaats naar ${effectivePc.pcLabel}`
                  : `Start op ${effectivePc.pcLabel}`
                : 'Start'}
          </button>
        </footer>
      </div>

      {pickerOpen ? <QueuePickerModal onClose={() => setPickerOpen(false)} /> : null}
    </div>
  );
}
